
import Vue from 'vue';
import { db, functions } from '@/firebase';
import {
	Avatar,
	Runnit,
	RunnitNode,
	RunnitNodeDef,
	AVATAR_USE_CASE,
	IMAGE_UPLOAD_MODE,
	CompanyCustomFieldDef,
	CompanyCustomFieldKey,
	RUNNIT_TYPE,
	RUNNITS_ACCESS_LEVEL,
	RUNNIT_NODE_RUN_STATE,
} from '@run-diffusion/shared';
import _trim from 'lodash/trim';
import _cloneDeep from 'lodash/cloneDeep';
import _filter from 'lodash/filter';
import _includes from 'lodash/includes';
import _toLower from 'lodash/toLower';
import _find from 'lodash/find';
import { mapActions, mapGetters, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { ROUTER } from '@/router/constants';
import {
	DEFAULT_TEXT_MAX_LENGTH,
	getShowTextCounterAt,
	getTextMaxLengthRule,
} from '@/utils';
import GreyButton from '@/components/base/GreyButton.vue';
import HeroImageGallery from '@/views/Runnits/base/HeroImageGallery.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { RunnitsCRUDMixin, TeamUserMixin, TEMPLATE_MAX_RESULTS_PER_NODE } from '@/mixins';
import DialogOrBottomSheet from '../../components/base/DialogOrBottomSheet.vue';
import HtmlField from '@/components/HtmlField.vue';
import ListItem from '@/components/base/ListItem.vue';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import ToolOnboardingTopCard from '@/components/ImageGallery/ToolOnboardingTopCard.vue';
import RunnitNodeSettingsDialog from '@/views/Runnits/RunnitNodeSettingsDialog.vue';
import RunnitToolsLibraryDialog from '@/views/Runnits/RunnitToolsLibraryDialog.vue';
import TemplateGenerationSelector from '@/components/TemplateGenerationSelector/TemplateGenerationSelector.vue';
import {
	RUNNIT_TOOL_SETTINGS_VALUES,
	RUNNIT_TOOL_SETTINGS_MENU_ITEMS,
	FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_NODES_LIMIT,
	RUNNITS_OWNER_SELECTION,
} from '@/views/Runnits/constants';
import { isAtRunnitNodesLimit } from '@/utils/runnitNodesLimit';
import {
	initializeCloneFromSourceWorkflow,
	initializeCreateWorkflowNoSource,
	initializeEditWorkflowWithAvatarFallback,
} from '@/views/Runnits/template-dialog/workflow';
import {
	buildTemplatePayloadAdapter,
	extractCustomFieldsForOwnerSelection,
	buildCreatedRunnitStateSnapshot,
} from '@/views/Runnits/template-dialog/payloadAdapter';
import { shouldLoadExistingTemplateNodeRuns } from '@/views/Runnits/template-dialog/parityHarness';
import {
	applyTemplateNodeCloneStrategy,
	fetchResolvedRunnitNodesFromRunnit,
} from '@/views/Runnits/template-dialog/nodeLoader';
import { buildSelectedResultsFromNodeRuns } from '@/views/Runnits/template-dialog/existingNodeRuns';
import * as amplitude from '@amplitude/analytics-browser';
import { v4 as uuidv4 } from 'uuid';

export default Vue.extend({
	name: 'RunnitTemplateDialog',
	mixins: [
		RunnitsCRUDMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	data () {
		const SHORT_TEXT_LENGTH: number = 100;
		return {
			IMAGE_UPLOAD_MODE,
			AVATAR_USE_CASE,
			DEFAULT_TEXT_MAX_LENGTH,
			SHORT_TEXT_LENGTH,
			RUNNITS_OWNER_SELECTION,

			// Stepper
			currentStep: 1,
			TEMPLATE_DETAILS_STEP: 1,
			SELECT_TOOLS_STEP: 2,
			ASSIGN_GENERATIONS_STEP: 3,

			// Form data
			templateTitle: null,
			templateDescription: null,
			templateOnboardingData: {} as any,
			templateAvatarId: null,
			templateAvatar: null,
			templateCustomFields: {} as Record<CompanyCustomFieldKey, any>,

			// NodeDefs
			currentRunnitNodes: [] as RunnitNode[],
			availableNodeDefs: [] as RunnitNodeDef[],
			toolSearchQuery: '',
			loadingRunnitNodes: false,

			// Form validation
			templateFormValid: false,
			rules: {
				required: [
					v => !!v || 'Required field',
				],
				title: [
					getTextMaxLengthRule(SHORT_TEXT_LENGTH),
					v => !!_trim(v) || 'Required',
				],
				description: [
					getTextMaxLengthRule(),
				],
				customField: [
					getTextMaxLengthRule(),
				],
			},

			saving: false,
			isUploading: false,

			// Drag and drop state
			isDraggingNodeDef: false,
			draggedNodeDef: null,
			draggedNodeDefIndex: null,

			// Tool onboarding dialog
			toolOnboardingDialog: {
				open: false,
				selectedNode: null as RunnitNode | null,
			},

			// Node settings dialog
			nodeSettingsDialog: {
				open: false,
				selectedNode: null as RunnitNode | null,
			},

			// Template tools dialog
			templateToolsDialog: {
				open: false,
			},
			addingToolLoading: false,

			// Selected results for template (Step 3)
			selectedResults: [] as Array<{
				templateNodeId: string
				sourceNodeRunId: string
				resultUuid: string
				result: any
			}>,
		};
	},
	watch: {
		'runnitState.createRunnitTemplateOpen': {
			immediate: true,
			async handler (newVal: boolean, oldVal: boolean) {
				if (newVal && !oldVal) {
					// Wait a tick to ensure runnitState.runnit is set (handles race condition when both are set in same update)
					await this.$nextTick();
					await this.initializeTemplate();
				}
			},
		},
		'runnitState.runnit': {
			immediate: false,
			async handler (newVal: any, oldVal: any) {
				// Re-initialize if dialog is open and runnit changed (handles case where runnit is set after dialog opens)
				if (
					this.runnitState.createRunnitTemplateOpen &&
					newVal &&
					newVal !== oldVal &&
					newVal.id &&
					newVal.id !== oldVal?.id
				) {
					await this.$nextTick();
					await this.initializeTemplate();
				}
			},
		},
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'team',
			'publicRunnitNodeDefs',
			'companyRunnitNodeDefs',
			'runnitNodes',
		]),
		...mapGetters([
			'runnitNumNodesLimit',
		]),
		isEditMode (): boolean {
			return !!(
				this.runnitState.runnit &&
				this.runnitState.runnit.type === RUNNIT_TYPE.TEMPLATE &&
				this.runnitState.runnit.id &&
				!this.runnitState.duplicateTemplate
			);
		},
		runnitCustomFieldDefs (): CompanyCustomFieldDef[] {
			return this.team?.company?.runnitCustomFieldDefs || [];
		},
		canCreateTemplate (): boolean {
			return this.currentRunnitNodes.length > 0 && this.currentRunnitNodes.length <= this.runnitNumNodesLimit;
		},
		isAtNodesLimit (): boolean {
			return isAtRunnitNodesLimit((this.currentRunnitNodes || []).length, this.runnitNumNodesLimit);
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
		]),
		getShowTextCounterAt,
		async resetTemplateFormValidation () {
			await this.$nextTick();
			const form = this.$refs.templateForm as undefined | { resetValidation?: () => void };
			form?.resetValidation?.();
		},
		async initializeTemplate () {
			// Reset stepper to first step
			this.currentStep = 1;

			if (!this.runnitState.runnit) {
				const snapshot = await initializeCreateWorkflowNoSource(() => this.getRandomDefaultAvatar());
				this.templateTitle = snapshot.templateTitle;
				this.templateDescription = snapshot.templateDescription;
				this.templateOnboardingData = snapshot.templateOnboardingData;
				this.templateCustomFields = snapshot.templateCustomFields;
				this.currentRunnitNodes = snapshot.currentRunnitNodes;
				this.selectedResults = snapshot.selectedResults;
				this.templateAvatarId = snapshot.templateAvatarId;
				this.templateAvatar = snapshot.templateAvatar;
				await this.resetTemplateFormValidation();
				return;
			}

			const runnit = this.runnitState.runnit;

			const snapshot = this.isEditMode
				? await initializeEditWorkflowWithAvatarFallback(runnit, () => this.getRandomDefaultAvatar())
				: await initializeCloneFromSourceWorkflow(runnit, () => this.getRandomDefaultAvatar());

			this.templateTitle = snapshot.templateTitle;
			this.templateDescription = snapshot.templateDescription;
			this.templateOnboardingData = snapshot.templateOnboardingData;
			this.templateCustomFields = snapshot.templateCustomFields;
			this.templateAvatarId = snapshot.templateAvatarId;
			this.templateAvatar = snapshot.templateAvatar;

			await this.loadCurrentRunnitNodes();
			if (shouldLoadExistingTemplateNodeRuns({
				isEditMode: this.isEditMode,
				duplicateTemplate: this.runnitState.duplicateTemplate,
				runnitType: runnit.type,
			})) {
				await this.loadExistingNodeRuns();
			}
			await this.resetTemplateFormValidation();
		},
		async loadCurrentRunnitNodes () {
			const runnit = this.runnitState.runnit;
			if (!runnit || !runnit.id) return;

			this.loadingRunnitNodes = true;

			try {
				const { nodes: rawNodes } = await fetchResolvedRunnitNodesFromRunnit(runnit.id);

				if (!rawNodes || !rawNodes.length) {
					this.currentRunnitNodes = [];
					return;
				}

				const { nodes } = applyTemplateNodeCloneStrategy(rawNodes, this.isEditMode, () => uuidv4());
				this.currentRunnitNodes = nodes;
			} catch (error) {
				console.error('Error loading runnit nodes:', error);
			} finally {
				this.loadingRunnitNodes = false;
			}
		},
		updateCustomField (fieldKey: CompanyCustomFieldKey, value: any) {
			this.templateCustomFields = {
				...this.templateCustomFields,
				[fieldKey]: value,
			};
		},
		setDialog (isOpen) {
			this.updateRunnitState({
				createRunnitTemplateOpen: isOpen || false,
				...(!isOpen && { duplicateTemplate: false }),
			});
		},
		async goToNextStep () {
			if (this.currentStep === this.TEMPLATE_DETAILS_STEP) {
				await this.$nextTick();
				const form = this.$refs.templateForm as undefined | { validate?: () => boolean };
				if (!form?.validate || !form.validate()) {
					return;
				}
			}
			if (this.currentStep < this.ASSIGN_GENERATIONS_STEP) {
				this.currentStep++;
			}
		},
		goToPreviousStep () {
			if (this.currentStep > this.TEMPLATE_DETAILS_STEP) {
				this.currentStep--;
			}
		},
		handleImageUpload (avatar: Avatar) {
			this.templateAvatarId = this._get(avatar, 'id');
			this.templateAvatar = avatar;
		},
		handleImageSelected (avatar: Avatar) {
			this.templateAvatarId = avatar.id;
			this.templateAvatar = avatar;
		},
		determineTemplateMenuItems (node: RunnitNode, nodeIndex: number) {
			const onlyOneNodeExists: boolean = this.currentRunnitNodes.length === 1;
			let menuItems: any[] = [...RUNNIT_TOOL_SETTINGS_MENU_ITEMS];

			if (onlyOneNodeExists) return menuItems;

			if (!menuItems.length) return menuItems;
			const nextNode: RunnitNode = this._get(this.currentRunnitNodes, `[${+nodeIndex + 1}]`) || null;
			const canMoveUp: boolean = +nodeIndex > 0;
			const canMoveDown: boolean = +nodeIndex < (this.currentRunnitNodes.length - 1);

			// Find the edit info item and its index
			const editInfoItem = _find(menuItems, ['value', RUNNIT_TOOL_SETTINGS_VALUES.EDIT_INFO]);
			const insertIndex: number = editInfoItem ? menuItems.indexOf(editInfoItem) + 1 : menuItems.length;
			let lastId: number = menuItems[menuItems.length - 1].id;

			// Insert move up / down controls after EDIT_INFO
			if (canMoveUp) {
				lastId += 1;
				menuItems.splice(insertIndex, 0, {
					id: lastId,
					icon: 'mdi-chevron-up',
					label: 'Move Tool Up',
					description: '',
					value: RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP,
					needsCanEditRunnitPermission: false,
				});
			}
			if (canMoveDown) {
				lastId += 1;
				menuItems.splice(insertIndex + +canMoveUp, 0, {
					id: lastId,
					icon: 'mdi-chevron-down',
					label: 'Move Tool Down',
					description: '',
					value: RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN,
					needsCanEditRunnitPermission: false,
				});
			}
			return menuItems;
		},
		async onSelectedSettingMenuItem (selectedMenuItem: string, selectedNode: RunnitNode, nodeIndex: number) {
			switch (selectedMenuItem) {
				case RUNNIT_TOOL_SETTINGS_VALUES.VIEW_INFO:
					// Show tool onboarding dialog
					this.openToolOnboardingDialog(selectedNode);
					break;

				case RUNNIT_TOOL_SETTINGS_VALUES.EDIT_INFO:
					// Open node settings dialog to edit title/description
					this.nodeSettingsDialog = {
						open: true,
						selectedNode,
					};
					break;

				case RUNNIT_TOOL_SETTINGS_VALUES.DELETE:
					// Remove the node from the template
					this.currentRunnitNodes.splice(nodeIndex, 1);
					this.currentRunnitNodes = _cloneDeep(this.currentRunnitNodes); // to trigger reactivity
					break;

				case RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP:
				case RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN:
					await this.moveTemplateNode(selectedMenuItem, selectedNode, nodeIndex);
					break;

				default:
					console.warn('Unknown menu item:', selectedMenuItem);
					break;
			}
		},
		async moveTemplateNode (selectedMenuItem: string, node: RunnitNode, nodeIndex: number) {
			// Can't move up the first node or move down the last node
			if (
				(
					selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP &&
					nodeIndex <= 0
				) ||
				(
					selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN &&
					nodeIndex >= this.currentRunnitNodes.length - 1
				)
			) {
				return;
			}

			// Swap the node with the one before it or after it
			if (selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP) {
				const tempNode = this.currentRunnitNodes[nodeIndex - 1];
				this.currentRunnitNodes[nodeIndex - 1] = this.currentRunnitNodes[nodeIndex];
				this.currentRunnitNodes[nodeIndex] = tempNode;
			} else if (selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN) {
				const tempNode = this.currentRunnitNodes[nodeIndex + 1];
				this.currentRunnitNodes[nodeIndex + 1] = this.currentRunnitNodes[nodeIndex];
				this.currentRunnitNodes[nodeIndex] = tempNode;
			}
			this.currentRunnitNodes = _cloneDeep(this.currentRunnitNodes); // to trigger reactivity
		},

		async onCreateTemplate () {
			if (!this.$refs.templateForm.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			if (!this.canCreateTemplate) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please select at least one tool for the template',
					show: true,
				});
				return;
			}

			let createdTemplate: Runnit | null = null;

			try {
				this.saving = true;

				await this.updateLoader({
					show: true,
					message: 'Creating template, please wait...',
				});

				const customFields = extractCustomFieldsForOwnerSelection({
					ownerSelection: this.runnitState.runnitsOwnerSelection,
					fieldDefs: this.runnitCustomFieldDefs,
					templateCustomFields: this.templateCustomFields,
				}) as Record<CompanyCustomFieldKey, any>;

				const teamId = this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? (this.team?.id || null) : null;

				const templateData: Partial<Runnit> = {
					createdAt: new Date(),
					deletedAt: null,
					userId: this.user.id,
					teamId,
					title: this.templateTitle,
					description: this.templateDescription,
					onboardingData: this.templateOnboardingData,
					avatarId: this.templateAvatarId,
					avatar: this.templateAvatarId ? db.doc(`avatars/${this.templateAvatarId}`) : null,
					...(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && { customFields }),
					type: RUNNIT_TYPE.TEMPLATE,
					accessLevel: RUNNITS_ACCESS_LEVEL.PRIVATE, // Templates are private by default
				} as unknown as Partial<Runnit>;

				const createRunnitTemplateRef = functions.httpsCallable('createRunnitTemplate');
				const payload = buildTemplatePayloadAdapter({
					mode: 'create',
					teamId,
					templateTitle: this.templateTitle,
					templateDescription: this.templateDescription,
					templateAvatarId: this.templateAvatarId,
					templateOnboardingData: this.templateOnboardingData,
					customFields,
					nodes: this.currentRunnitNodes.map((node: RunnitNode) => ({
						id: node.id,
						nodeDefId: node.nodeDefId,
						title: node.title,
						description: node.description,
					})),
					selectedResults: this.selectedResults || [],
				});

				const { data } = await createRunnitTemplateRef(payload);

				if (data?.success && data?.templateId) {
					createdTemplate = buildCreatedRunnitStateSnapshot(templateData, data.templateId) as Runnit;

					amplitude.track(`Template Created`);

					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: 'Template created successfully!',
						show: true,
					});

					this.setDialog(false);

					// Navigate to the board templates page
					this.$router.push({ name: ROUTER.RUNNIT_BOARD_TEMPLATES_PAGE });

					// Check if user has permission to manage access and open visibility dialog
					// Wait for next tick to ensure navigation is complete before popping the dialog
					this.$nextTick(() => {
						if (
							createdTemplate?.id &&
							this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
							this.hasShareRunnitAssetsPermission
						) {
							this.updateRunnitState({
								runnit: createdTemplate,
								runnitManageAccessOpen: true,
							});
						}
					});
				}
				else {
					throw new Error(data?.error || 'createRunnitTemplate did not return success');
				}
			} catch (e) {
				console.error('Error creating template', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error creating template, please try again',
					show: true,
				});
			} finally {
				this.saving = false;
				await this.updateLoader({
					show: false,
					message: null,
				});
			}

			return createdTemplate;
		},
		async onEditTemplate () {
			if (!this.$refs.templateForm.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			if (!this.canCreateTemplate) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please select at least one tool for the template',
					show: true,
				});
				return;
			}

			let updatedTemplate: Runnit | null = null;

			try {
				this.saving = true;

				await this.updateLoader({
					show: true,
					message: 'Saving template, please wait...',
				});

				const customFields = extractCustomFieldsForOwnerSelection({
					ownerSelection: this.runnitState.runnitsOwnerSelection,
					fieldDefs: this.runnitCustomFieldDefs,
					templateCustomFields: this.templateCustomFields,
				}) as Record<CompanyCustomFieldKey, any>;

				const templateId = this.runnitState.runnit.id;
				const updateData: Partial<Runnit> = {
					title: this.templateTitle,
					description: this.templateDescription,
					onboardingData: this.templateOnboardingData,
					avatarId: this.templateAvatarId,
					avatar: this.templateAvatarId ? db.doc(`avatars/${this.templateAvatarId}`) : null,
					...(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && { customFields }),
				} as unknown as Partial<Runnit>;

				const updateRunnitTemplateRef = functions.httpsCallable('updateRunnitTemplate');
				const payload = buildTemplatePayloadAdapter({
					mode: 'edit',
					templateId,
					teamId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? (this.team?.id || null) : null,
					templateTitle: this.templateTitle,
					templateDescription: this.templateDescription,
					templateAvatarId: this.templateAvatarId,
					templateOnboardingData: this.templateOnboardingData,
					customFields,
					nodes: this.currentRunnitNodes.map((node: RunnitNode) => ({
						id: node.id,
						nodeDefId: node.nodeDefId,
						title: node.title,
						description: node.description,
					})),
					selectedResults: this.selectedResults || [],
				});

				const { data } = await updateRunnitTemplateRef(payload);
				if (!data?.success || !data?.templateId) {
					throw new Error(data?.error || 'updateRunnitTemplate did not return success');
				}

				updatedTemplate = {
					...this.runnitState.runnit,
					...updateData,
					id: templateId,
				} as Runnit;

				amplitude.track(`Template Updated`);

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Template saved successfully!',
					show: true,
				});

				this.setDialog(false);
			} catch (e) {
				console.error('Error updating template', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error saving template, please try again',
					show: true,
				});
			} finally {
				this.saving = false;
				await this.updateLoader({
					show: false,
					message: null,
				});
			}

			return updatedTemplate;
		},
		openToolOnboardingDialog (node: RunnitNode) {
			this.toolOnboardingDialog = {
				open: true,
				selectedNode: node,
			};
		},
		closeToolOnboardingDialog () {
			this.toolOnboardingDialog = {
				open: false,
				selectedNode: null,
			};
		},
		onRunnitNodeSettingsClose () {
			this.nodeSettingsDialog = {
				open: false,
				selectedNode: null,
			};
		},
		onNodeUpdated (updatedNode: RunnitNode) {
			// Find and update the node in currentRunnitNodes
			const nodeIndex = this.currentRunnitNodes.findIndex((node: RunnitNode) => node.id === updatedNode.id);
			if (nodeIndex !== -1) {
				this.currentRunnitNodes[nodeIndex] = updatedNode;
				// Trigger reactivity
				this.currentRunnitNodes = _cloneDeep(this.currentRunnitNodes);
			}
		},
		openTemplateToolsDialog () {
			this.templateToolsDialog = {
				open: true,
			};
		},
		onTemplateToolsDialogClose () {
			this.templateToolsDialog = {
				open: false,
			};
		},
		async onTemplateToolSelected (runnitNodeDef: RunnitNodeDef) {
			try {
				this.addingToolLoading = true;

				// Create a mock RunnitNode from the RunnitNodeDef
				const mockRunnitNode = this.createMockRunnitNode(runnitNodeDef);

				// Add to currentRunnitNodes
				this.currentRunnitNodes.push(mockRunnitNode);
				this.currentRunnitNodes = _cloneDeep(this.currentRunnitNodes); // Trigger reactivity

				// Scroll to the bottom of the tools list after the new tool is added
				this.$nextTick(() => {
					this.scrollToBottomOfToolsList();
				});

				this.onTemplateToolsDialogClose();
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error adding tool to template, please try again',
					show: true,
				});
			} finally {
				this.addingToolLoading = false;
			}
		},
		scrollToBottomOfToolsList () {
			// Scroll to the "Add Another Tool" button at the bottom
			const addToolButton = this.$refs.addToolButton;
			if (addToolButton) {
				addToolButton.scrollIntoView({
					behavior: 'smooth',
					block: 'center'
				});
			}
		},
		createMockRunnitNode (runnitNodeDef: RunnitNodeDef): Partial<RunnitNode> {
			return {
				id: uuidv4(), // Always generate new ID for newly added nodes
				title: runnitNodeDef.title,
				description: runnitNodeDef.description || '',
				sortOrder: this.currentRunnitNodes.length,
				nodeDefId: runnitNodeDef.id,
				nodeDef: runnitNodeDef,
			};
		},
		async loadExistingNodeRuns () {
			if ((!this.isEditMode && !this.runnitState.duplicateTemplate) || !this.runnitState.runnit?.id) return;

			// Ensure nodes are loaded first
			if (!this.currentRunnitNodes || !this.currentRunnitNodes.length) {
				await this.loadCurrentRunnitNodes();
			}

			// Verify nodes have valid IDs
			const nodesWithValidIds = this.currentRunnitNodes.filter(n => n && n.id);
			if (!nodesWithValidIds.length) {
				console.error('loadExistingNodeRuns: No nodes with valid IDs found');
				return;
			}

			try {
				const maxNodeRunsOnTemplate = FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_NODES_LIMIT * TEMPLATE_MAX_RESULTS_PER_NODE;
				this.selectedResults = await buildSelectedResultsFromNodeRuns({
					runnitId: this.runnitState.runnit.id,
					templateNodes: nodesWithValidIds,
					maxNodeRuns: maxNodeRunsOnTemplate,
					doneState: RUNNIT_NODE_RUN_STATE.DONE,
				});
			} catch (error) {
				console.error('Error loading existing nodeRuns:', error);
			}
		},
		onResultsSelected (results: Array<{
			templateNodeId: string
			sourceNodeRunId: string
			resultUuid: string
			result: any
		}>) {
			this.selectedResults = results;
		},
	},
	components: {
		ActionsIsland,
		HeroImageGallery,
		GreyButton,
		ImageUpload,
		RunnitImage,
		DialogOrBottomSheet,
		HtmlField,
		ListItem,
		GlassButton,
		BaseStyledMenu,
		ToolOnboardingTopCard,
		RunnitNodeSettingsDialog,
		RunnitToolsLibraryDialog,
		TemplateGenerationSelector,
	},
});
